import { ChangeDetectionStrategy, Component, Inject, OnDestroy } from '@angular/core'
import { formatDisplayName } from 'libs/shared/utils/helpers/src/lib/format-display-name'
import { FormControl } from '@angular/forms'
import { MAT_DIALOG_DATA } from '@angular/material/dialog'
import { ProfilesEntity, UsersFacade } from '@galabid/gb-dashboard/user/data-access'
import * as UsersActions from 'libs/gb-dashboard/user/data-access/src/lib/+state/users.actions'
import { SharedProfile } from 'libs/shared/models/profile/profile.models'
import { PaddleRaisePledgesEntity } from 'libs/shared/paddle-numbers/data-access-paddle-raise-pledges/src/lib/+state/paddle-raise-pledges.models'
import { GbNotificationService } from 'libs/shared/utils/notification/src/lib/service/gb-notification.service'
import { map } from 'rxjs'
import { SelectedAuctionFacade } from 'libs/gb-dashboard/auctions/data-access-selected-auction/src/lib/+state/selected-auction.facade'

@Component({
  selector: 'gb-assign-paddle-dialog',
  templateUrl: './gb-assign-paddle-dialog.component.html',
  styleUrl: './gb-assign-paddle-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class GbAssignPaddleDialogComponent implements OnDestroy {
  pledge: PaddleRaisePledgesEntity

  userControl = new FormControl()
  loading$ = this.usersFacade.loaded$.pipe(map((loaded) => !loaded))

  profiles$ = this.usersFacade.allProfiles$.pipe(
    map((profiles) => profiles.map((profile) => ({ ...profile, label: this.getProfileLabel(profile) }))),
  )

  updateUserLoaded$ = this.usersFacade.editCreateLoaded$
  requirePaymentMethod$ = this.selectedAuctionFacade.auction$.pipe(
    map((auction) => auction?.paddle_numbers_require_payment_method),
  )

  constructor(
    @Inject(MAT_DIALOG_DATA) data: PaddleRaisePledgesEntity,
    private gbNotificationService: GbNotificationService,
    private usersFacade: UsersFacade,
    private selectedAuctionFacade: SelectedAuctionFacade,
  ) {
    this.pledge = data
  }

  ngOnDestroy(): void {
    this.usersFacade.dispatch(UsersActions.clearSelectedUser())
  }

  getProfileLabel(profile: SharedProfile): string {
    if (profile.paddle_number) {
      return `${formatDisplayName(profile)} (${profile.paddle_number})`
    }

    return formatDisplayName(profile)
  }

  onSelectUser(user: ProfilesEntity | undefined) {
    if (!user) return this.onClear()

    this.usersFacade.dispatch(
      UsersActions.selectUser({
        uuid: user.uuid,
      }),
    )
  }

  onClear() {
    this.usersFacade.dispatch(UsersActions.clearSelectedUser())
  }

  updateGuest(requirePaymentMethod: boolean) {
    if (!this.userControl.value) {
      return this.gbNotificationService.error('SHARED.PADDLE_NUMBER.ASSIGN_DIALOG.SELECT_AND_RETRY')
    }

    if (this.showErrorMessage(requirePaymentMethod)) {
      return
    }

    if (this.userControl.value.paddle_number) {
      return this.gbNotificationService.error('SHARED.PADDLE_NUMBER.ASSIGN_DIALOG.ALREADY_PADDLE_NUMBER', '', {
        paddleNumber: this.userControl.value.paddle_number,
      })
    }

    this.usersFacade.dispatch(
      UsersActions.assignPaddleNumber({
        user: {
          ...this.userControl.value,
          paddle_number: this.pledge.paddle_number,
        },
      }),
    )
  }

  showErrorMessage(requirePaymentMethod: boolean): boolean {
    const user = this.userControl.value

    if (!user) {
      return false
    }

    if (!user.user) {
      return true
    }
    if (!user.has_payment_method && requirePaymentMethod) {
      return true
    }

    return false
  }

  getErrorMessage(requirePaymentMethod: boolean): string | undefined {
    const user = this.userControl.value

    if (!user) {
      return undefined
    }

    const hasInvitePhone = !!user.invite_phone_number
    const hasInviteEmail = !!user.invite_email
    const usePhoneMessage = hasInvitePhone && !hasInviteEmail

    if (!user?.user && requirePaymentMethod && !user.has_payment_method) {
      return usePhoneMessage
        ? 'SHARED.PADDLE_NUMBER.PROFILE_UNVERIFIED_PHONE_PAYMENT_METHOD'
        : 'SHARED.PADDLE_NUMBER.PROFILE_UNVERIFIED_EMAIL_PAYMENT_METHOD'
    }
    if (!!user?.user && requirePaymentMethod && !user.has_payment_method) {
      return 'SHARED.PADDLE_NUMBER.PROFILE_NO_PAYMENT_METHOD'
    }

    return usePhoneMessage
      ? 'SHARED.PADDLE_NUMBER.PROFILE_UNVERIFIED_PHONE'
      : 'SHARED.PADDLE_NUMBER.PROFILE_UNVERIFIED_EMAIL'
  }

  errorString(requirePaymentMethod: boolean): string {
    return this.getErrorMessage(requirePaymentMethod) as string
  }
}
